import React, {FC} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {ReactComponent as SpawnLogo} from '../svgs/spawn-logo.svg';
import {ReactComponent as DetailedSpawnLogo} from '../svgs/detailed-spawn-logo.svg';
import {ReactComponent as ArrowIcon} from '../svgs/arrow-icon.svg';
import {ReactComponent as LogoutIcon} from '../svgs/logout-icon.svg';
import {useAuth} from "../providers/AuthProvider";
import {useToast} from "use-toast-mui";
import {useScreenSize} from "../hooks/useScreenSize";

interface IScreenWrapperProps {
    hasLogoutButton?: boolean;
    children: React.ReactNode;
}

export const ScreenWrapper: FC<IScreenWrapperProps> = ({hasLogoutButton = false, children}) => {
    const {logOut} = useAuth()
    const {isScreenLessThan360, isScreenLessThan600} = useScreenSize()
    const toast = useToast()

    return (
        !isScreenLessThan360 ?
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                gap: '.62rem',
                mx: 'auto',
                py: '.5rem',
                backgroundColor: '#000',
            }}>
                {
                    hasLogoutButton &&
                    <Box sx={{
                        display: 'flex',
                        width: isScreenLessThan600 ? '90vw' : '31.25rem',
                        padding: '1rem 1.25rem',
                        marginRight: { xs: '2rem', md: 0 },
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: '.62rem',
                        flexShrink: '0',
                    }}>
                        <Button aria-label="log-out"
                                onClick={async () => {
                                    try {
                                        await logOut();
                                    } catch (error) {
                                        toast.error('Error signing out.');
                                    }
                                }}
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.70)',
                                    leadingTrim: 'both',
                                    textEdge: 'cap',
                                    fontFamily: 'Montserrat',
                                    fontSize: '.87rem',
                                    fontStyle: 'normal',
                                    fontWeight: '600',
                                    lineHeight: 'normal',
                                    textTransform: 'none',
                                    display: 'flex',
                                    gap: '.62rem',
                                }}>
                            Logout <LogoutIcon fontSize="inherit"/>
                        </Button>
                    </Box>
                }
                <Box sx={{
                    display: 'flex',
                    width: isScreenLessThan600 ? '90vw' : '31.25rem',
                    minHeight: '46.87rem',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexShrink: '0',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    background: 'rgba(255, 255, 255, 0.05)',
                }}>
                    <Box sx={{
                        display: 'flex',
                        padding: '2.5rem',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        borderBottom: '1px solid #262626',
                    }}>
                        <Typography sx={{
                            color: 'rgba(255, 255, 255, 0.30)',
                            textAlign: 'center',
                            leadingTrim: 'both',
                            textEdge: 'cap',
                            fontFamily: 'Montserrat, serif',
                            fontSize: '1.25rem',
                            fontStyle: 'normal',
                            fontWeight: '600',
                            lineHeight: 'normal',
                            marginRight: '2rem'
                        }}>Customer Portal</Typography>
                        <SpawnLogo/>
                    </Box>
                    {children}
                    <Box
                        sx={{
                            display: 'flex',
                            padding: '1.87rem 2.5rem',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '.62rem',
                            alignSelf: 'stretch',
                            borderTop: '1px solid #262626',
                        }}
                    >
                        <DetailedSpawnLogo/>
                        <Box
                            component="a"
                            href="mailto:support@saucewareaudio.com"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                color: 'rgba(255, 255, 255, 0.50)',
                                gap: '5px',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'inherit',
                                    textAlign: 'right',
                                    fontFamily: 'Inter, serif',
                                    fontSize: '.87rem',
                                    fontStyle: 'normal',
                                    fontWeight: '400',
                                    lineHeight: 'normal',
                                }}
                            >
                                Email Customer Service
                            </Typography>
                            <ArrowIcon/>
                        </Box>
                    </Box>
                </Box>
            </Box> :
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                mx: 'auto',
                backgroundColor: '#000',
            }}>
                <Typography sx={{
                    color: 'rgba(255, 255, 255, 0.50)',
                    fontFamily: 'Inter',
                    mx: '1.87rem',
                    textAlign: 'center',
                }}>To use this app, your browser window must be wider than 360px. If you are on your phone, try turning it sideways.</Typography>
            </Box>
    );
};
